import { ApolloQueryResult } from '@apollo/client';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAnalyticTotalGrowth,
  ISeriesLineChart,
  IValuePieChart
} from 'src/models/general';
import { IGroupSentiment } from 'src/models/comparison';
import { ChronologicalList } from 'src/utils/buildDataLineGroup';

interface IOverview {
  analyticTotalEngagement: ApolloQueryResult<IAnalyticTotalGrowth | undefined>;
  analyticTotalFollowers: ApolloQueryResult<IAnalyticTotalGrowth | undefined>;
  analyticTotalTalk: ApolloQueryResult<IAnalyticTotalGrowth | undefined>;
  analyticTotalReach: ApolloQueryResult<IAnalyticTotalGrowth | undefined>;
  analyticTotalOutreach: ApolloQueryResult<IAnalyticTotalGrowth | undefined>;
  chronologicalEngagementPeformance: ApolloQueryResult<
    Array<ISeriesLineChart> | undefined
  >;
  chronologicalFollowersGrowth: ApolloQueryResult<
    Array<ChronologicalList> | undefined
  >;
  chronologicalReachPeformance: ApolloQueryResult<
    Array<ISeriesLineChart> | undefined
  >;
  chronologicalTalkPeformance: ApolloQueryResult<
    Array<ISeriesLineChart> | undefined
  >;
  chronologicalSocmedPeformance: ApolloQueryResult<
    Array<ISeriesLineChart> | undefined
  >;
  shareOfVoiceByCreatorType: ApolloQueryResult<
    Array<IValuePieChart> | undefined
  >;
  sentimentByCreatorType: ApolloQueryResult<
    Array<IGroupSentiment> | undefined
  >;
}

const initialState: IOverview = {
  analyticTotalEngagement: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  analyticTotalFollowers: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  analyticTotalTalk: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  analyticTotalReach: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  analyticTotalOutreach: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  chronologicalEngagementPeformance: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  chronologicalFollowersGrowth: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  chronologicalReachPeformance: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  chronologicalTalkPeformance: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  chronologicalSocmedPeformance: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  shareOfVoiceByCreatorType: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  sentimentByCreatorType: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  }
};

export const sosmedOverview = createSlice({
  name: 'sosmedOverview',
  initialState,
  reducers: {
    reducerUpdateAnalyticTotalEngagement: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<IAnalyticTotalGrowth>>
    ) => {
      state.analyticTotalEngagement = action.payload;
    },
    reducerUpdateAnalyticTotalFollowers: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<IAnalyticTotalGrowth>>
    ) => {
      state.analyticTotalFollowers = action.payload;
    },
    reducerUpdateAnalyticTotalTalk: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<IAnalyticTotalGrowth>>
    ) => {
      state.analyticTotalTalk = action.payload;
    },
    reducerUpdateAnalyticTotalReach: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<IAnalyticTotalGrowth>>
    ) => {
      state.analyticTotalReach = action.payload;
    },
    reducerUpdateAnalyticTotalOutreach: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<IAnalyticTotalGrowth>>
    ) => {
      state.analyticTotalOutreach = action.payload;
    },
    reducerUpdateChronologicalEngagementPeformance: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<ISeriesLineChart>>>
    ) => {
      state.chronologicalEngagementPeformance = action.payload;
    },
    reducerUpdateChronologicalFollowersGrowth: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<ChronologicalList>>>
    ) => {
      state.chronologicalFollowersGrowth = action.payload;
    },
    reducerUpdateChronologicalEngagementLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.chronologicalEngagementPeformance.loading = action.payload;
    },
    reducerUpdateChronologicalFollowersLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.chronologicalFollowersGrowth.loading = action.payload;
    },
    reducerUpdateChronologicalTalkPeformance: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<ISeriesLineChart>>>
    ) => {
      state.chronologicalTalkPeformance = action.payload;
    },
    reducerUpdateChronologicalTalkLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.chronologicalTalkPeformance.loading = action.payload;
    },
    reducerUpdateChronologicalReachPeformance: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<ISeriesLineChart>>>
    ) => {
      state.chronologicalReachPeformance = action.payload;
    },
    reducerUpdateChronologicalReachLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.chronologicalReachPeformance.loading = action.payload;
    },

    reducerUpdateChronologicalSocmedPeformance: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<ISeriesLineChart>>>
    ) => {
      state.chronologicalSocmedPeformance = action.payload;
    },
    reducerUpdateChronologicalSocmedLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.chronologicalSocmedPeformance.loading = action.payload;
    },
    reducerUpdateShareOfVoiceByCreatorType: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<IValuePieChart>>>
    ) => {
      state.shareOfVoiceByCreatorType = action.payload;
    },
    reducerUpdateShareOfVoiceByCreatorTypeLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.shareOfVoiceByCreatorType.loading = action.payload;
    },
    reducerUpdateSentimentByCreatorType: (
      state: IOverview,
      action: PayloadAction<ApolloQueryResult<Array<IGroupSentiment>>>
    ) => {
      state.sentimentByCreatorType = action.payload;
    },
    reducerUpdateSentimentByCreatorTypeLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.sentimentByCreatorType.loading = action.payload;
    },
    reducerUpdateTotalEngagementLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.analyticTotalEngagement.loading = action.payload;
    },
    reducerUpdateAllMetricLoading: (
      state: IOverview,
      action: PayloadAction<boolean>
    ) => {
      state.analyticTotalEngagement.loading = action.payload;
      state.analyticTotalFollowers.loading = action.payload;
      state.analyticTotalReach.loading = action.payload;
      state.analyticTotalOutreach.loading = action.payload;
      state.analyticTotalTalk.loading = action.payload;
      state.chronologicalEngagementPeformance.loading = action.payload;
      state.chronologicalFollowersGrowth.loading = action.payload;
      state.chronologicalReachPeformance.loading = action.payload;
      state.chronologicalTalkPeformance.loading = action.payload;
      state.chronologicalSocmedPeformance.loading = action.payload;
      state.shareOfVoiceByCreatorType.loading = action.payload;
      state.sentimentByCreatorType.loading = action.payload;
    }
  }
});

export const {
  reducerUpdateAllMetricLoading,
  reducerUpdateAnalyticTotalEngagement,
  reducerUpdateAnalyticTotalFollowers,
  reducerUpdateAnalyticTotalReach,
  reducerUpdateAnalyticTotalOutreach,
  reducerUpdateAnalyticTotalTalk,
  reducerUpdateChronologicalEngagementPeformance,
  reducerUpdateChronologicalFollowersGrowth,
  reducerUpdateChronologicalReachLoading,
  reducerUpdateChronologicalReachPeformance,
  reducerUpdateChronologicalSocmedLoading,
  reducerUpdateChronologicalSocmedPeformance,
  reducerUpdateChronologicalTalkPeformance,
  reducerUpdateChronologicalTalkLoading,
  reducerUpdateChronologicalEngagementLoading,
  reducerUpdateChronologicalFollowersLoading,
  reducerUpdateSentimentByCreatorType,
  reducerUpdateSentimentByCreatorTypeLoading,
  reducerUpdateShareOfVoiceByCreatorType,
  reducerUpdateShareOfVoiceByCreatorTypeLoading,
  reducerUpdateTotalEngagementLoading
} = sosmedOverview.actions;

export default sosmedOverview.reducer;
